import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Play, Lock, Crown, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Showcase = () => {
  const { user, isAuthenticated } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, [user]);

  const fetchVideos = async () => {
    try {
      const endpoint = isAuthenticated ? '/api/showcase/videos' : '/api/showcase/videos/public';
      const response = await fetch(`${API_URL}${endpoint}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setVideos(data);
        if (data.length > 0 && !selectedVideo) {
          setSelectedVideo(data[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const canViewVideo = (video) => {
    if (video.visibility === 'public') return true;
    if (!user) return false;
    if (user.is_admin) return true;
    if (video.visibility === 'pro' && ['pro', 'enterprise'].includes(user.plan)) return true;
    if (video.visibility === 'enterprise' && user.plan === 'enterprise') return true;
    return false;
  };

  const getVisibilityBadge = (visibility) => {
    switch (visibility) {
      case 'enterprise':
        return <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">Enterprise</span>;
      case 'pro':
        return <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">Pro</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/vritta-logo.png" alt="Vrittaa AI" className="w-8 h-8" />
            <span className="text-xl font-bold font-outfit text-white tracking-wider">VRITTAA</span>
          </Link>

          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 py-12 sm:py-16 px-4 sm:px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-emerald-400 mb-4 block">
            Product Showcase
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-outfit text-white mb-4">
            See Vrittaa AI <span className="text-gradient">in Action</span>
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Watch demo videos showcasing our powerful automation capabilities and agentic workflows.
          </p>
        </motion.div>
      </section>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        {videos.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8 text-slate-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No Videos Yet</h3>
            <p className="text-slate-400">Check back soon for demo videos!</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Video Player */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden"
              >
                {selectedVideo && canViewVideo(selectedVideo) ? (
                  <div className="aspect-video">
                    <iframe
                      src={selectedVideo.embed_url}
                      title={selectedVideo.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  </div>
                ) : selectedVideo ? (
                  <div className="aspect-video bg-slate-800 flex flex-col items-center justify-center">
                    <Lock className="w-12 h-12 text-slate-600 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">Premium Content</h3>
                    <p className="text-slate-400 mb-4">Upgrade to {selectedVideo.visibility} plan to watch</p>
                    <Link to="/pricing">
                      <Button className="bg-emerald-600 hover:bg-emerald-500 text-white">
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade Now
                      </Button>
                    </Link>
                  </div>
                ) : null}
                
                {selectedVideo && (
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-xl font-bold text-white mb-2">{selectedVideo.title}</h2>
                        <p className="text-slate-400">{selectedVideo.description}</p>
                      </div>
                      {getVisibilityBadge(selectedVideo.visibility)}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Video List */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">All Videos</h3>
              {videos.map((video) => (
                <motion.button
                  key={video.video_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => setSelectedVideo(video)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedVideo?.video_id === video.video_id
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="flex gap-4">
                    <div className="relative w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-800">
                      {!canViewVideo(video) && (
                        <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center">
                          <Lock className="w-5 h-5 text-slate-500" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Play className="w-6 h-6 text-white/80" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-white truncate">{video.title}</h4>
                        {getVisibilityBadge(video.visibility)}
                      </div>
                      <p className="text-sm text-slate-400 line-clamp-2">{video.description}</p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        {!isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16 text-center"
          >
            <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Start Automating?</h3>
              <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                Create a free account to access more features and unlock premium demo videos.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/signup">
                  <Button className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-6">
                    Start Free Trial
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 px-8 py-6">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Showcase;
